import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Plus } from "lucide-react"
import { AppLayout } from "../../../components/layout/AppLayout"
import { Button } from "../../../components/ui/button"

type ClientSummary = {
  id: number
  cliente: string
  razao_social_tomador: string
}

type FiscalSettingRow = {
  id: number
  municipio_prestacao: string
  company_name: string
  omie_servico_label: string
  omie_conta_label: string
  codigo_servico_nota: string
  omie_c_cod_serv_mun: string
  omie_c_cod_serv_lc116: string
  numero_contrato: string
  irrf: string
  pis: string
  cofins: string
  iss: string
  csll: string
  irrf_withhold: boolean
  pis_withhold: boolean
  cofins_withhold: boolean
  iss_withhold: boolean
  csll_withhold: boolean
  created_at: string
}

type Props = {
  client: ClientSummary
  fiscal_settings: FiscalSettingRow[]
}

function pct(v: string) {
  const n = Number(v)
  return Number.isFinite(n) ? `${n}%` : "-"
}

function retainLabel(flags: { ir: boolean; pi: boolean; co: boolean; is: boolean; cs: boolean }) {
  const parts: string[] = []
  if (flags.ir) parts.push("IRRF")
  if (flags.pi) parts.push("PIS")
  if (flags.co) parts.push("COFINS")
  if (flags.is) parts.push("ISS")
  if (flags.cs) parts.push("CSLL")
  return parts.length ? parts.join(", ") : "—"
}

export default function FiscalSettingsIndex({ client, fiscal_settings }: Props) {
  const base = `/clientes/${client.id}/configuracoes-fiscais`

  return (
    <>
      <Head title={`Configurações fiscais – ${client.cliente}`} />
      <AppLayout>
        <div className="mx-auto w-full max-w-6xl p-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                <Link href="/clientes">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Configurações fiscais</h1>
                <p className="text-slate-400">
                  {client.cliente} — {client.razao_social_tomador}
                </p>
              </div>
            </div>
            <Button asChild>
              <Link href={`${base}/nova`}>
                <Plus className="mr-2 h-4 w-4" />
                Nova configuração
              </Link>
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Município</th>
                  <th className="px-4 py-3 font-medium">Empresa (Omie)</th>
                  <th className="px-4 py-3 font-medium">Serviço Omie</th>
                  <th className="px-4 py-3 font-medium">Conta Omie</th>
                  <th className="px-4 py-3 font-medium">Cód. serviço NF</th>
                  <th className="px-4 py-3 font-medium">Omie mun / LC116</th>
                  <th className="px-4 py-3 font-medium">Contrato</th>
                  <th className="px-4 py-3 font-medium">Alíquotas</th>
                  <th className="px-4 py-3 font-medium">Retenções</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {fiscal_settings.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-400" colSpan={10}>
                      Nenhuma configuração fiscal. Clique em &quot;Nova configuração&quot;.
                    </td>
                  </tr>
                ) : (
                  fiscal_settings.map((row) => (
                    <tr key={row.id} className="border-b border-slate-800/80 last:border-b-0">
                      <td className="px-4 py-3 text-slate-200">{row.municipio_prestacao || "—"}</td>
                      <td className="max-w-[140px] px-4 py-3 text-xs text-slate-300">{row.company_name || "—"}</td>
                      <td className="max-w-[200px] px-4 py-3 text-xs text-slate-300" title={row.omie_servico_label}>
                        {row.omie_servico_label || "—"}
                      </td>
                      <td className="max-w-[200px] px-4 py-3 text-xs text-slate-300" title={row.omie_conta_label}>
                        {row.omie_conta_label || "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-200">{row.codigo_servico_nota}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">
                        {[row.omie_c_cod_serv_mun || "—", row.omie_c_cod_serv_lc116 || "—"].join(" · ")}
                      </td>
                      <td className="px-4 py-3 text-slate-200">{row.numero_contrato || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-300">
                        IRRF {pct(row.irrf)} · PIS {pct(row.pis)} · COFINS {pct(row.cofins)} · ISS {pct(row.iss)} · CSLL{" "}
                        {pct(row.csll)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-300">
                        {retainLabel({
                          ir: row.irrf_withhold,
                          pi: row.pis_withhold,
                          co: row.cofins_withhold,
                          is: row.iss_withhold,
                          cs: row.csll_withhold
                        })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                          <Link href={`${base}/${row.id}/editar`}>Editar</Link>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AppLayout>
    </>
  )
}
